import React, { useEffect, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { aggregatorsApi, AggregatorSettings, RevenueSource } from '../../api/aggregators';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { sectionNote, sectionTitle } from './shared';

/**
 * The rules. Each applies from the moment it is saved: an earning keeps the
 * rate and sources it was booked under, a withdrawal the tax it was taken at.
 */
export const SettingsPanel: React.FC<{ settings: AggregatorSettings; onSaved: (s: AggregatorSettings) => void }> = ({
  settings,
  onSaved,
}) => {
  const toast = useToast();
  const { can } = useAuth();
  const editable = can('aggregators.settings');

  const [open, setOpen] = useState(settings.applicationsOpen);
  const [rate, setRate] = useState(String(settings.defaultCommissionPercent));
  const [lifetime, setLifetime] = useState(settings.earningWindowMonths === null);
  const [months, setMonths] = useState(String(settings.earningWindowMonths ?? 12));
  const [selfDealing, setSelfDealing] = useState(settings.selfDealing);
  const [tax, setTax] = useState(String(settings.withholdingTaxPercent));
  const [minimum, setMinimum] = useState(String(settings.minimumWithdrawal));
  const [sources, setSources] = useState<RevenueSource[]>(settings.revenueSources);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setOpen(settings.applicationsOpen);
    setRate(String(settings.defaultCommissionPercent));
    setLifetime(settings.earningWindowMonths === null);
    setMonths(String(settings.earningWindowMonths ?? 12));
    setSelfDealing(settings.selfDealing);
    setTax(String(settings.withholdingTaxPercent));
    setMinimum(String(settings.minimumWithdrawal));
    setSources(settings.revenueSources);
  }, [settings]);

  const rateN = Number(rate);
  const monthsN = Number(months);
  const taxN = Number(tax);
  const minimumN = Number(minimum);
  const errors = {
    rate: rateN > 0 && rateN <= 50 ? undefined : 'Above 0% and at most 50%.',
    months: lifetime || (Number.isInteger(monthsN) && monthsN >= 1 && monthsN <= 120) ? undefined : '1 to 120 months.',
    tax: tax.trim() !== '' && taxN >= 0 && taxN <= 25 ? undefined : '0% to 25%.',
    minimum: minimumN >= 1 && minimumN <= 1_000_000 ? undefined : '₦1 to ₦1,000,000.',
    sources: sources.length ? undefined : 'Choose at least one.',
  };
  const valid = Object.values(errors).every((e) => !e);

  async function save() {
    setSaving(true);
    try {
      const saved = await aggregatorsApi.updateSettings({
        applicationsOpen: open,
        defaultCommissionPercent: rateN,
        earningWindowMonths: lifetime ? null : monthsN,
        selfDealing,
        withholdingTaxPercent: taxN,
        minimumWithdrawal: minimumN,
        revenueSources: sources,
      });
      onSaved(saved);
      toast.success('Aggregator rules saved.');
    } catch (err) {
      toast.error(err, 'Could not save the rules.');
    } finally {
      setSaving(false);
    }
  }

  const label: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' };
  const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 };

  return (
    <Card padding={20}>
      <h3 style={sectionTitle}>Rules</h3>
      <p style={sectionNote}>
        Changes apply from when you save. What has been earned keeps the rate and rules it was earned under.
      </p>
      <fieldset disabled={!editable} style={{ border: 0, padding: 0, margin: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={label}>Applications</span>
            <label style={row}>
              <input type="checkbox" checked={open} onChange={(e) => setOpen(e.target.checked)} />
              Open. People can apply from Invite friends.
            </label>
          </div>

          <Input
            label="Default share (%)"
            hint="Offered when you approve without choosing a rate."
            type="number"
            step="0.01"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            error={errors.rate}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={label}>How long a business earns for them</span>
            <label style={row}>
              <input type="radio" checked={lifetime} onChange={() => setLifetime(true)} />
              For as long as it uses PayIQ
            </label>
            <label style={row}>
              <input type="radio" checked={!lifetime} onChange={() => setLifetime(false)} />
              For
              <input
                aria-label="Months"
                type="number"
                min={1}
                max={120}
                value={months}
                disabled={lifetime || !editable}
                onChange={(e) => setMonths(e.target.value)}
                style={{ width: 70, height: 30, padding: '0 8px', fontSize: 13 }}
              />
              months after it joins
            </label>
            {errors.months ? <span style={{ fontSize: 12, color: 'var(--danger-600)' }}>{errors.months}</span> : null}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={label}>A business that looks like their own</span>
            <label style={row}>
              <input type="radio" checked={selfDealing === 'flag'} onChange={() => setSelfDealing('flag')} />
              Flag it for review. It earns until someone decides.
            </label>
            <label style={row}>
              <input type="radio" checked={selfDealing === 'block'} onChange={() => setSelfDealing('block')} />
              Block it. It earns nothing.
            </label>
          </div>

          <Input
            label="Withholding tax (%)"
            hint="Taken from each withdrawal and kept for remittance."
            type="number"
            step="0.01"
            value={tax}
            onChange={(e) => setTax(e.target.value)}
            error={errors.tax}
          />

          <Input
            label="Smallest withdrawal (₦)"
            type="number"
            value={minimum}
            onChange={(e) => setMinimum(e.target.value)}
            error={errors.minimum}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={label}>Revenue they earn a share of</span>
            {settings.availableSources.map((s) => (
              <label key={s.key} style={row}>
                <input
                  type="checkbox"
                  checked={sources.includes(s.key)}
                  onChange={(e) =>
                    setSources((prev) => (e.target.checked ? [...prev, s.key] : prev.filter((x) => x !== s.key)))
                  }
                />
                {s.label}
              </label>
            ))}
            {errors.sources ? <span style={{ fontSize: 12, color: 'var(--danger-600)' }}>{errors.sources}</span> : null}
          </div>
        </div>
      </fieldset>
      {editable ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <Button onClick={() => void save()} disabled={saving || !valid}>
            {saving ? 'Saving…' : 'Save rules'}
          </Button>
        </div>
      ) : (
        <p style={{ ...sectionNote, marginTop: 16 }}>Changing these needs the aggregator rules permission.</p>
      )}
    </Card>
  );
};
